import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form'
import styled from '@emotion/styled'

import LabeledInput, { LabeledInputProps } from '@/components/LabeledInput'
import { Flex } from '@/components/Flex'
import HelpWithTooltip from '@/components/HelpWithTooltip'
import {
  ControlLabel,
  TooltipText,
  ControlErrorMessage,
} from '@/components/Typography'
import { colors } from '../constants/colors'

const SimpleTextArea = styled.textarea<{ hasError?: boolean }>`
  display: flex;
  color: ${colors.white};
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%;
  padding: 1.25rem;
  width: 100%;
  align-items: center;
  border: 1px solid ${colors.darkBlue2};
  border-radius: 16px;
  background-color: ${colors.darkBlue2};
  outline: none;
  overflow: hidden;
  min-height: 10rem;
  resize: none;
  word-break: break-all;
  overflow-wrap: normal;
  padding-right: 22px;
  padding-bottom: 22px;
  transition:
    border 0.25s ease-out,
    box-shadow 0.25s ease-out,
    transform 0.1s ease-out;

  &::placeholder {
    color: ${colors.light2};
    opacity: 1;
  }

  &:focus {
    outline: none;
    border: 1px solid ${colors.blue};
    box-shadow: 0 0 0 4px rgba(0, 125, 255, 0.15);
  }

  &:active:not(&:disabled) {
    transform: scale(0.995);
  }

  &:disabled {
    opacity: 0.5;
  }

  ${({ hasError }) =>
    hasError &&
    `
    border: 1px solid ${colors.red};
    
    &:focus:not(&:disabled) {
      box-shadow: 0 0 0 4px rgba(255, 69, 0, 0.15);
    }
  `}
`

// Utility functions for multiline processing
const stringToMultiline = (value: string): string => {
  if (!value) return ''
  return value.split(',').map(s => s.trim()).join('\n')
}

const multilineToString = (value: string): string => {
  return value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join(', ')
}

export function TrimmedInputField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  multiline = false,
  ...props
}: LabeledInputProps &
  Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
    multiline?: boolean
  }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        if (multiline) {
          // Multiline mode: convert comma-separated to line-separated for display
          const displayValue = stringToMultiline(field.value || '')
          
          return (
            <Flex flexDirection="column" gap="0.625rem" width="100%">
              {typeof props.label === 'string' ? (
                <Flex gap="0.5rem">
                  <ControlLabel>{props.label}</ControlLabel>
                  {props.hint && (
                    <HelpWithTooltip
                      fontSize="10px"
                      size="1rem"
                      tooltipContent={<TooltipText>{props.hint}</TooltipText>}
                    />
                  )}
                </Flex>
              ) : (
                props.label
              )}
              <Flex flexDirection="column" gap="0.3125rem" width="100%">
                <SimpleTextArea
                  value={displayValue}
                  onChange={(e) => {
                    const converted = multilineToString(e.target.value)
                    field.onChange(converted)
                  }}
                  onBlur={field.onBlur}
                  hasError={!!fieldState.error}
                  placeholder={props.placeholder}
                />
                {fieldState.error && (
                  <ControlErrorMessage>
                    {fieldState.error.message}
                  </ControlErrorMessage>
                )}
              </Flex>
            </Flex>
          )
        }

        // Single line mode: simple text input with trimming
        return (
          <LabeledInput
            value={field.value || ''}
            inputMode="text"
            onChange={(e) => {
              field.onChange(e.target.value.trim())
            }}
            onBlur={field.onBlur}
            error={fieldState.error ? fieldState.error.message : undefined}
            {...props}
          />
        )
      }}
    />
  )
}
